<script setup lang="ts">
import { computed, ref } from 'vue';
import { useTemplateRefsList } from '@vueuse/core';
import type { ApplicantEntry } from '@/types/Applicant';
import type { CompanyDetails, TrustDetails } from '@/types';
import { TrusteeType, ApplicantType } from '@/helpers/const';
import { getFullName } from '@/helpers/format';
import { createApplicant } from '@/helpers/builder';
import TrustBasicForm from './TrustBasicForm.vue';
import CompanyForm from './CompanyForm.vue';
import PersonCard from './PersonCard.vue';

const props = withDefaults(
  defineProps<{
    allIndividuals?: ApplicantEntry[];
    initialTrustDetails?: TrustDetails;
    initialCompanyDetails?: CompanyDetails;
    initialDirectors?: ApplicantEntry[];
    initialTrustees?: ApplicantEntry[];
    isPrimaryBorrower?: boolean;
    forceShowErrors?: boolean;
  }>(),
  {
    allIndividuals: () => [],
    initialTrustDetails: () => ({}),
    initialCompanyDetails: () => ({}),
    initialDirectors: () => [],
    initialTrustees: () => [],
    isPrimaryBorrower: false,
    forceShowErrors: false,
  },
);

const emit = defineEmits<{
  (e: 'relationship-change', individualId: string, newStatus: string, oldStatus?: string): void;
  (e: 'sync-applicant', applicant: ApplicantEntry): void;
  (e: 'remove-applicant', applicantId: string): void;
}>();

const validationError = ref('');

const trustDetails = ref<TrustDetails>({ ...props.initialTrustDetails });
const trustBasicFormRef = ref<InstanceType<typeof TrustBasicForm>>();

const companyDetails = ref<CompanyDetails>({ ...props.initialCompanyDetails });
const directors = ref<ApplicantEntry[]>(
  props.initialDirectors.map((d, index) => ({
    ...d,
    type: props.isPrimaryBorrower && index === 0 ? ApplicantType.PRIMARY : d.type,
  })),
);
const directorRefs = useTemplateRefsList<InstanceType<typeof PersonCard>>();
const companyFormRef = ref<InstanceType<typeof CompanyForm>>();

const trustees = ref<ApplicantEntry[]>(
  props.initialTrustees.map((t, index) => ({
    ...t,
    type: props.isPrimaryBorrower && index === 0 ? ApplicantType.PRIMARY : t.type,
  })),
);
const trusteeRefs = useTemplateRefsList<InstanceType<typeof PersonCard>>();

const showTrustForm = ref(!props.initialTrustDetails?.trustName);
const showCompanyForm = ref(!props.initialCompanyDetails?.companyName);

// Use trustDetails.value (stable) instead of vee-validate form values (resets on re-mount)
const isCompanyTrustee = computed(() => trustDetails.value.trusteeType === TrusteeType.COMPANY);
const isIndividualTrustee = computed(() => trustDetails.value.trusteeType === TrusteeType.INDIVIDUAL);
const isIndividualTrusteeSelected = computed(
  () => trustBasicFormRef.value?.values?.trusteeType === TrusteeType.INDIVIDUAL,
);
const isCompanyTrusteeSelected = computed(() => trustBasicFormRef.value?.values?.trusteeType === TrusteeType.COMPANY);
const hasAnySavedDirector = computed(() => directors.value.some((d) => d.firstName && d.lastName));
const hasAnySavedTrustee = computed(() => trustees.value.some((t) => t.firstName && t.lastName));
const excludedDirectorIds = computed(() => directors.value.filter((d) => d.firstName && d.lastName).map((d) => d.id));
const excludedTrusteeIds = computed(() => trustees.value.filter((t) => t.firstName && t.lastName).map((t) => t.id));

const isCompanySaved = computed(
  () => isCompanyTrustee.value && !showCompanyForm.value && Boolean(companyDetails.value.companyName),
);

const isTrustBasicSaved = computed(
  () => isIndividualTrustee.value && !showTrustForm.value && Boolean(trustDetails.value.trustName),
);

const displayName = computed(() => {
  const trustName = trustDetails.value.trustName || '';

  if (isCompanyTrustee.value) {
    const companyName = companyDetails.value.companyName || '';
    if (companyName && trustName) {
      return `${companyName} ATF ${trustName}`;
    }
    return trustName || companyName;
  }

  if (isIndividualTrustee.value) {
    const savedTrustees = trustees.value.filter((t) => t.firstName && t.lastName);
    const trusteeNames = savedTrustees.map((t) => getFullName(t)).join(' & ');
    if (trusteeNames && trustName) {
      return `${trusteeNames} ATF ${trustName}`;
    }
    return trustName || trusteeNames;
  }

  return trustName;
});

const addDirector = () => {
  const isFirstDirector = directors.value.length === 0;
  const type = props.isPrimaryBorrower && isFirstDirector ? ApplicantType.PRIMARY : ApplicantType.SECONDARY;
  const newDirector = createApplicant(type);
  directors.value = [...directors.value, newDirector];
};

const removeDirector = (directorId: string) => {
  const director = directors.value.find((d) => d.id === directorId);
  const wasSaved = director?.firstName && director?.lastName;
  directors.value = directors.value.filter((d) => d.id !== directorId);
  if (directors.value.length === 0) {
    showCompanyForm.value = true;
  }
  if (wasSaved) {
    emit('remove-applicant', directorId);
  }
};

const saveCompanyAndAddFirstDirector = async () => {
  // Validate trust basic form first to populate trustDetails before saving company
  const trustValid = (await trustBasicFormRef.value?.update()) ?? false;
  if (!trustValid) {
    return false;
  }

  const companyValid = (await companyFormRef.value?.update()) ?? false;
  if (companyValid) {
    showTrustForm.value = false;
    showCompanyForm.value = false;
    if (directors.value.length === 0) {
      addDirector();
    }
  }
  return companyValid;
};

const editCompanyDetails = () => {
  showCompanyForm.value = true;
};

const cancelCompanyEdit = () => {
  companyFormRef.value?.cancel();
  if (companyDetails.value.companyName && hasAnySavedDirector.value) {
    showCompanyForm.value = false;
  }
};

const addTrustee = () => {
  const isFirstTrustee = trustees.value.length === 0;
  const type = props.isPrimaryBorrower && isFirstTrustee ? ApplicantType.PRIMARY : ApplicantType.SECONDARY;
  const newTrustee = createApplicant(type);
  trustees.value = [...trustees.value, newTrustee];
};

const removeTrustee = (trusteeId: string) => {
  const trustee = trustees.value.find((t) => t.id === trusteeId);
  const wasSaved = trustee?.firstName && trustee?.lastName;
  trustees.value = trustees.value.filter((t) => t.id !== trusteeId);
  if (trustees.value.length === 0) {
    showTrustForm.value = true;
  }
  if (wasSaved) {
    emit('remove-applicant', trusteeId);
  }
};

const saveTrustAndAddFirstTrustee = async () => {
  const trustValid = (await trustBasicFormRef.value?.update()) ?? false;
  if (trustValid) {
    showTrustForm.value = false;
    if (trustees.value.length === 0) {
      addTrustee();
    }
  }
  return trustValid;
};

const editTrustDetails = () => {
  showTrustForm.value = true;
};

const cancelTrustEdit = () => {
  trustBasicFormRef.value?.cancel();
  if (trustDetails.value.trustName && hasAnySavedTrustee.value) {
    showTrustForm.value = false;
  }
};

const update = async () => {
  validationError.value = '';

  if (showTrustForm.value || !trustDetails.value.trustName) {
    const trustBasicValid = (await trustBasicFormRef.value?.update()) ?? false;
    if (!trustBasicValid) {
      return false;
    }
  }

  if (isCompanyTrustee.value) {
    const isCompanyFormVisible = showCompanyForm.value || directors.value.length === 0;
    if (isCompanyFormVisible) {
      const companyValid = (await companyFormRef.value?.update()) ?? false;
      if (!companyValid) {
        return false;
      }
    }

    const hasCompanyName = Boolean(companyDetails.value.companyName);
    const hasSavedDirector = directors.value.some((d) => d.firstName && d.lastName);

    if (hasCompanyName && !hasSavedDirector) {
      validationError.value = 'At least one director needs to be nominated';
    }

    if (directors.value.length > 0) {
      const directorValidations = await Promise.all(
        directorRefs.value.filter((ref) => ref?.update).map((ref) => ref.update()),
      );
      if (!directorValidations.every(Boolean)) {
        return false;
      }
    }

    return hasCompanyName && hasSavedDirector;
  }

  if (isIndividualTrustee.value) {
    const hasSavedTrustee = trustees.value.some((t) => t.firstName && t.lastName);

    if (!hasSavedTrustee) {
      validationError.value = 'At least one trustee needs to be nominated';
    }

    if (trustees.value.length > 0) {
      const trusteeValidations = await Promise.all(
        trusteeRefs.value.filter((ref) => ref?.update).map((ref) => ref.update()),
      );
      if (!trusteeValidations.every(Boolean)) {
        return false;
      }
    }

    return hasSavedTrustee;
  }

  return false;
};

const cancel = () => {
  trustBasicFormRef.value?.cancel();
  companyFormRef.value?.cancel();
  trustDetails.value = {};
  companyDetails.value = {};
  directors.value = [];
  trustees.value = [];
  showCompanyForm.value = true;
  showTrustForm.value = true;
};

const expandDirector = (directorId: string): boolean => {
  const index = directors.value.findIndex((d) => d.id === directorId);
  if (index >= 0 && directorRefs.value[index]) {
    directorRefs.value[index].expandAndFocus();
    return true;
  }
  return false;
};

const expandTrustee = (trusteeId: string): boolean => {
  const index = trustees.value.findIndex((t) => t.id === trusteeId);
  if (index >= 0 && trusteeRefs.value[index]) {
    trusteeRefs.value[index].expandAndFocus();
    return true;
  }
  return false;
};

const expandIndividual = (individualId: string): boolean => {
  if (isCompanyTrustee.value) {
    return expandDirector(individualId);
  }
  if (isIndividualTrustee.value) {
    return expandTrustee(individualId);
  }
  return false;
};

defineExpose({
  update,
  cancel,
  displayName,
  isCompanySaved,
  isTrustBasicSaved,
  editCompanyDetails,
  editTrustDetails,
  trustDetails,
  companyDetails,
  directors,
  trustees,
  expandIndividual,
});
</script>

<template>
  <div class="trust-section">
    <div v-if="!isTrustBasicSaved && !isCompanySaved">
      <TrustBasicForm ref="trustBasicFormRef" v-model="trustDetails" data-test-id="trust-basic-form" />

      <div v-if="isIndividualTrusteeSelected || isIndividualTrustee" class="mt-8 flex-shrink-0">
        <BxButton id="trust-save" data-test-id="trust-save" variant="secondary" @click="saveTrustAndAddFirstTrustee()">
          Save
        </BxButton>
        <BxButton
          v-if="trustDetails.trustName"
          data-test-id="trust-cancel"
          variant="link"
          class="ml-4"
          @click="cancelTrustEdit()"
        >
          Cancel
        </BxButton>
      </div>
    </div>

    <template v-if="isCompanyTrustee || isCompanyTrusteeSelected">
      <div v-if="showCompanyForm || directors.length === 0">
        <CompanyForm ref="companyFormRef" v-model="companyDetails" data-test-id="trust-company-form" />
        <div class="mt-8 flex-shrink-0">
          <BxButton
            id="trust-company-save"
            data-test-id="trust-company-save"
            variant="secondary"
            @click="saveCompanyAndAddFirstDirector()"
          >
            Save
          </BxButton>
          <BxButton
            v-if="companyDetails.companyName"
            data-test-id="trust-company-cancel"
            variant="link"
            class="ml-4"
            @click="cancelCompanyEdit()"
          >
            Cancel
          </BxButton>
        </div>
      </div>

      <div v-if="directors.length > 0 && !showCompanyForm" class="directors-section space-y-4">
        <PersonCard
          v-for="(director, index) in directors"
          :key="director.id"
          :ref="directorRefs.set"
          v-model="directors[index]"
          :data-test-id="`personcard-director-${index}`"
          :index="index"
          role="director"
          :all-individuals="[...props.allIndividuals, ...directors, ...trustees]"
          :current-id="director.id"
          :force-show-errors="props.forceShowErrors"
          :excluded-applicant-ids="excludedDirectorIds"
          @remove="removeDirector"
          @relationship-change="(individualId: string, newStatus: string, oldStatus?: string) => emit('relationship-change', individualId, newStatus, oldStatus)"
          @sync-applicant="(applicant: ApplicantEntry) => emit('sync-applicant', applicant)"
        />
        <div v-if="hasAnySavedDirector" class="pt-2">
          <BxButton id="trust-add-director" data-test-id="trust-add-director" variant="tertiary" @click="addDirector()">
            + Add director
          </BxButton>
        </div>
      </div>
    </template>

    <div v-if="isTrustBasicSaved && trustees.length > 0" class="trustees-section space-y-4">
      <PersonCard
        v-for="(trustee, index) in trustees"
        :key="trustee.id"
        :ref="trusteeRefs.set"
        v-model="trustees[index]"
        :data-test-id="`personcard-trustee-${index}`"
        :index="index"
        role="trustee"
        :all-individuals="[...props.allIndividuals, ...trustees, ...directors]"
        :current-id="trustee.id"
        :force-show-errors="props.forceShowErrors"
        :excluded-applicant-ids="excludedTrusteeIds"
        @remove="removeTrustee"
        @relationship-change="(individualId: string, newStatus: string, oldStatus?: string) => emit('relationship-change', individualId, newStatus, oldStatus)"
        @sync-applicant="(applicant: ApplicantEntry) => emit('sync-applicant', applicant)"
      />
      <div v-if="hasAnySavedTrustee" class="pt-2">
        <BxButton id="trust-add-trustee" data-test-id="trust-add-trustee" variant="tertiary" @click="addTrustee()">
          + Add trustee
        </BxButton>
      </div>
    </div>

    <BxErrorPanel v-if="validationError" class="mt-4">
      {{ validationError }}
    </BxErrorPanel>
  </div>
</template>

<style lang="scss" scoped>
.form-control {
  margin-top: 1.5rem;
}
</style>
